import { useCallback, useMemo } from 'react';
import { Language } from '../../../models/session';

import en from '../locales/en.json';
import de from '../locales/de.json';
import nl from '../locales/nl.json';
import pl from '../locales/pl.json';
import ru from '../locales/ru.json';
import hu from '../locales/hu.json';
import fr from '../locales/fr.json';
import ro from '../locales/ro.json';
import es from '../locales/es.json';
import it from '../locales/it.json';
import cs from '../locales/cs.json';
import bg from '../locales/bg.json';
import hr from '../locales/hr.json';
import tr from '../locales/tr.json';
import pt from '../locales/pt.json';
import lt from '../locales/lt.json';
import lv from '../locales/lv.json';
import ee from '../locales/ee.json';
import sk from '../locales/sk.json';
import sl from '../locales/sl.json';

type Dictionary = Record<string, string>;

export type TranslateFn = (
    key: string,
    params?: Record<string, string | number>,
) => string;

const DICTIONARIES: Record<Language, Dictionary> = {
    [Language.EN]: en,
    [Language.DE]: de,
    [Language.NL]: nl,
    [Language.PL]: pl,
    [Language.RU]: ru,
    [Language.HU]: hu,
    [Language.FR]: fr,
    [Language.RO]: ro,
    [Language.ES]: es,
    [Language.IT]: it,
    [Language.CS]: cs,
    [Language.BG]: bg,
    [Language.HR]: hr,
    [Language.TR]: tr,
    [Language.PT]: pt,
    [Language.LT]: lt,
    [Language.LV]: lv,
    [Language.EE]: ee,
    [Language.SK]: sk,
    [Language.SL]: sl,
};

/**
 * Tiny per-page i18n hook. Custom on purpose — `react-i18next` would add ~90KB
 * of dependencies for a single page that has ~40 strings to translate. The
 * lookup is build-time-resolved (JSON files imported directly, not fetched at
 * runtime) so missing-language files fail at compile time.
 *
 * Fallback chain: `current.json` → `en.json` → return key.
 * The key-as-fallback is intentionally loud — surfaces missing keys during
 * development rather than rendering an empty string.
 *
 * Interpolation: `t('hello', { name: 'Alice' })` replaces `{name}` in the
 * template. Keep tokens lowercase and short.
 */
export const useSessionFormI18n = (sessionLanguage: Language | null | undefined) => {
    const lang = sessionLanguage ?? Language.EN;
    const dict = DICTIONARIES[lang] ?? en;

    const t = useCallback(
        (key: string, params?: Record<string, string | number>): string => {
            const template = dict[key] ?? (en as Dictionary)[key] ?? key;
            if (!params) return template;
            return template.replace(/\{(\w+)\}/g, (_, token) => {
                const value = params[token];
                return value === undefined || value === null ? `{${token}}` : String(value);
            });
        },
        [dict],
    );

    return useMemo(() => ({ t, currentLanguage: lang }), [t, lang]);
};
